import type { Freshness } from './useFreshness.ts';

/**
 * queryState — a canonical, exhaustive UI state machine for data-fetch hooks
 * (A5-195).
 *
 * ## Why
 *
 * TanStack Query exposes a bag of orthogonal booleans (`isPending`, `isError`,
 * `isSuccess`, `isFetching`, `data`, `error`, `dataUpdatedAt`). Every component
 * re-derives "should I show a skeleton, a gray-out, an empty state, or an
 * error?" from those flags by hand, and they each get it slightly differently —
 * one renders a skeleton on background refetch, another blanks the screen, a
 * third shows stale data with no staleness cue. There is no single source of
 * truth for the rendering decision, and most hooks carry no explicit staleness
 * marker.
 *
 * {@link QueryState} collapses the flag bag into one discriminated union with a
 * `status` discriminant, so a component can `switch` on it exhaustively and the
 * type checker guarantees every branch is handled. {@link toQueryState} is the
 * pure adapter from a TanStack result; a view switches on `state.status` to pick
 * skeleton-vs-gray-out-vs-empty-vs-error consistently.
 *
 * This module is deliberately JSX-free so it can be unit-tested under Node's
 * `--experimental-strip-types` runner.
 */

/** Empty/loading/error classification for a data-fetch result. */
export type QueryStatus = 'idle' | 'pending' | 'success' | 'error';

/** Query has not started (e.g. a disabled query waiting on a parent select). */
export interface QueryStateIdle {
  status: 'idle';
}

/**
 * Query is loading with no usable data yet (first load → render a skeleton).
 * `isRefetch` distinguishes a true first load from a background refetch when no
 * prior data exists.
 */
export interface QueryStatePending {
  status: 'pending';
  isRefetch: boolean;
}

/**
 * Query has data. `freshnessAt` is the logical instant the data was last
 * updated (epoch ms, from `dataUpdatedAt`); `freshness` buckets it into the
 * shared fresh/aging/stale vocabulary; `isStale` is true when a background
 * refetch is in flight over already-shown data (gray-out, do NOT skeleton);
 * `isEmpty` is true when the payload is a structurally empty collection
 * (render an empty state, not a spinner).
 */
export interface QueryStateSuccess<T> {
  status: 'success';
  data: T;
  freshnessAt: number;
  freshness: Freshness;
  isStale: boolean;
  isEmpty: boolean;
}

/**
 * Query failed. `data` carries the last-known-good value when one exists, so a
 * transient refetch failure can keep showing stale data with an error banner
 * instead of blanking the screen; `isStale` is true whenever `data` is present.
 */
export interface QueryStateError<T> {
  status: 'error';
  error: unknown;
  data?: T;
  isStale: boolean;
}

export type QueryState<T> =
  | QueryStateIdle
  | QueryStatePending
  | QueryStateSuccess<T>
  | QueryStateError<T>;

const FRESH_S = 30;
const AGING_S = 5 * 60;

function classifyFreshness(ageMs: number): Freshness {
  const secs = ageMs / 1000;
  if (secs <= FRESH_S) return 'fresh';
  if (secs <= AGING_S) return 'aging';
  return 'stale';
}

/** Is the payload a structurally empty collection (array or {data:[]} envelope)? */
function isEmptyPayload(data: unknown): boolean {
  if (Array.isArray(data)) return data.length === 0;
  if (data && typeof data === 'object') {
    const env = data as { data?: unknown; items?: unknown; results?: unknown };
    for (const coll of [env.data, env.items, env.results]) {
      if (Array.isArray(coll)) return coll.length === 0;
    }
  }
  return false;
}

/**
 * The subset of a TanStack `UseQueryResult` that {@link toQueryState} needs.
 * Declared structurally so callers can pass the result straight through without
 * a peer dependency on the exact TanStack version's result type.
 */
export interface QueryResultLike<T> {
  data: T | undefined;
  error: unknown;
  isError: boolean;
  isPending: boolean;
  isFetching: boolean;
  /** TanStack `status`; we read it only to detect the disabled/idle case. */
  fetchStatus?: 'fetching' | 'paused' | 'idle';
  dataUpdatedAt: number;
}

/**
 * Pure adapter: collapse a TanStack query result into a {@link QueryState}.
 *
 * Decision order (mutually exclusive branches):
 *  1. error + no data  → `error` (blank-with-banner)
 *  2. error + data      → `error` carrying last-known-good (stale-with-banner)
 *  3. pending + disabled (fetchStatus idle, never fetched) → `idle`
 *  4. pending           → `pending` (skeleton); `isRefetch=false` on first load
 *  5. success           → `success` with freshness + isStale (background refetch)
 *                         + isEmpty (empty collection)
 *
 * @param now injectable clock for deterministic tests.
 */
export function toQueryState<T>(
  result: QueryResultLike<T>,
  now: number = Date.now(),
): QueryState<T> {
  const { data, error, isError, isPending, isFetching, fetchStatus, dataUpdatedAt } = result;

  if (isError) {
    if (data !== undefined) {
      return { status: 'error', error, data, isStale: true };
    }
    return { status: 'error', error, isStale: false };
  }

  if (isPending && data === undefined) {
    // A disabled query (enabled:false) reports isPending with fetchStatus idle
    // and was never fetched — that is "idle", not a loading skeleton.
    if (fetchStatus === 'idle' && dataUpdatedAt === 0) {
      return { status: 'idle' };
    }
    return { status: 'pending', isRefetch: false };
  }

  // data is defined here.
  const freshnessAt = dataUpdatedAt;
  return {
    status: 'success',
    data: data as T,
    freshnessAt,
    freshness: classifyFreshness(Math.max(0, now - freshnessAt)),
    // A background refetch over already-shown data: show the data, gray it out.
    isStale: isFetching,
    isEmpty: isEmptyPayload(data),
  };
}

/** Pull a human-readable message out of an arbitrary error shape. */
export function queryErrorMessage(error: unknown): string {
  if (!error) return 'Something went wrong.';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (typeof error === 'object') {
    const e = error as Record<string, unknown>;
    for (const k of ['detail', 'title', 'message', 'statusText']) {
      if (typeof e[k] === 'string' && (e[k] as string).length > 0) return e[k] as string;
    }
  }
  return 'Something went wrong.';
}
